/**
 * Shift pay that is knowable *before* a shift happens.
 *
 * Base pay is not `hourly × hours` — `level.rate2..16` / `level.london2..16` are whole-shift
 * amounts picked by the shift's hours band, with the short-call premium already baked into the
 * 2/3/4 hour bands. Rates are read live from the level, never snapshotted onto the shift, so the
 * caller is responsible for resolving the crew's level as-of the shift date before calling in.
 *
 * Deliberately excluded: extra hours, adjusted hours, finished-early and cancellation charges.
 * Those are only decided after the shift, so anything showing pay up front must leave them out.
 *
 * This module is pure: the database lookups (level as-of date, experience bonus, job_crew extras)
 * belong to the caller, so office and crew-api can share one copy of the arithmetic.
 */

/** A `level` row, or any object carrying its `rate2..16` / `london2..16` columns. */
export type ShiftRateSource = Record<string, unknown>

/** Hours bands that levels actually carry rates for. */
export const MIN_RATE_BAND_HOURS = 2
export const MAX_RATE_BAND_HOURS = 16

/**
 * Whole-shift rate for an hours band, or null when the level has no rate for it — a level with no
 * shift rates at all (Office, for example), or hours outside the 2–16 bands. Null means "we can't
 * work this out", which callers must surface as such rather than as £0.
 */
export const getShiftRate = (
  level: ShiftRateSource | null | undefined,
  hours: number,
  isLondonRate: boolean
): number | null => {
  if (!level || !hours) return null
  if (!Number.isInteger(hours) || hours < MIN_RATE_BAND_HOURS || hours > MAX_RATE_BAND_HOURS) return null

  const rate = level[`${isLondonRate ? 'london' : 'rate'}${hours}`]

  if (rate === null || rate === undefined) return null

  // Prisma hands back Decimal, raw SQL hands back strings — Number copes with both.
  const value = Number(rate)

  return Number.isFinite(value) ? value : null
}

/** London rates apply when either the crew member or the venue is in the London region. */
export const LONDON_REGION_ID = 1

export const isLondonRate = (crewRegion: number | null | undefined, venueRegion: number | null | undefined): boolean =>
  crewRegion === LONDON_REGION_ID || venueRegion === LONDON_REGION_ID

export type ShiftPayInput = {
  /** From getShiftRate — null when the rate can't be resolved. */
  basePay: number | null
  /** Cumulative per-hour experience bonus × hours. Zero from the Sept 2026 rate change onwards. */
  experienceBonus?: number | null
  /** Only pass this when the level earns OOT and the date isn't zero travel expense. */
  outOfTownBonus?: number | null
  mileageFuel?: number | null
  driverBonus?: number | null
  /** Only pass this when the crew member is chief on the date. */
  chiefBonus?: number | null
  skillsTotal?: number | null
}

export type ShiftPayBreakdown = {
  basePay: number
  experienceBonus: number
  outOfTownBonus: number
  mileageFuel: number
  driverBonus: number
  chiefBonus: number
  skillsTotal: number
  total: number
  /** True when the base rate couldn't be resolved — show "contact the office", not a total. */
  basePayUnknown: boolean
}

const amount = (value: number | null | undefined): number => {
  const number = Number(value ?? 0)

  return Number.isFinite(number) ? number : 0
}

/** Adds up the parts of a shift's pay, treating an unresolvable base rate as £0 but flagging it. */
export const buildShiftPay = (input: ShiftPayInput): ShiftPayBreakdown => {
  const basePay = amount(input.basePay)
  const experienceBonus = amount(input.experienceBonus)
  const outOfTownBonus = amount(input.outOfTownBonus)
  const mileageFuel = amount(input.mileageFuel)
  const driverBonus = amount(input.driverBonus)
  const chiefBonus = amount(input.chiefBonus)
  const skillsTotal = amount(input.skillsTotal)

  return {
    basePay,
    experienceBonus,
    outOfTownBonus,
    mileageFuel,
    driverBonus,
    chiefBonus,
    skillsTotal,
    total: basePay + experienceBonus + outOfTownBonus + mileageFuel + driverBonus + chiefBonus + skillsTotal,
    basePayUnknown: input.basePay === null || input.basePay === undefined,
  }
}
